import React from 'react';

import styled from 'styled-components';

import AiCiCategoryCriteriaTable, { Criterion } from './AiCiCategoryCriteriaTable';
import RankingsTable from './RankingsTable';
import RemainingInsightSections from './RemainingInsightSections';
import ScoringResultsTable from './ScoringResultsTable';
import { unwrap } from './utils';
import { AICompetitiveIntelligenceRankingRow, AIInsightSolution } from '../../types/recommendations';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  a {
    color: var(--dark-blue);
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
  }
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Heading = styled.div`
  font-size: 24px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Summary = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

type Props = {
  structure: AIInsightSolution;
  communityId?: number;
  communityName?: string;
};

const RecommendationStructuredView = ({ structure, communityId, communityName }: Props) => {
  const titleValue = unwrap(structure.title);
  const summaryValue = unwrap(structure.summary);
  const categoryValue = unwrap(structure.category);
  const criteria = unwrap(structure.criteria) as Criterion[] | undefined;
  const rankingTable = unwrap(structure.ranking_table);
  const scoringResultsTable = unwrap(structure.scoring_results_table);

  const hasCriteria = Array.isArray(criteria) && criteria.length > 0;
  const hasRankingTable = Array.isArray(rankingTable) && rankingTable.length > 0;
  const hasScoringResults = Array.isArray(scoringResultsTable) && scoringResultsTable.length > 0;
  const primaryRanking = hasRankingTable
    ? (rankingTable as AICompetitiveIntelligenceRankingRow[]).find((row) => row.community === communityName)
    : undefined;
  const rankingSummary =
    typeof titleValue === 'string'
      ? titleValue
      : primaryRanking && typeof categoryValue === 'string'
        ? `You are #${primaryRanking.rank} in ${categoryValue}.`
        : null;

  return (
    <Container>
      {(rankingSummary || summaryValue) && (
        <HeaderBlock>
          {rankingSummary && <Heading>{rankingSummary}</Heading>}
          {typeof summaryValue === 'string' && <Summary>{summaryValue}</Summary>}
        </HeaderBlock>
      )}

      {hasRankingTable && <RankingsTable rows={rankingTable} primaryCommunityName={communityName} />}

      {hasCriteria && (
        <AiCiCategoryCriteriaTable title="Scoring Criteria" criteria={criteria as Criterion[]} variant="list" />
      )}

      {hasScoringResults && hasCriteria && (
        <ScoringResultsTable
          rows={scoringResultsTable}
          criteria={criteria as Criterion[]}
          primaryCommunityName={communityName}
        />
      )}

      {hasCriteria && (
        <AiCiCategoryCriteriaTable
          title="Score Breakdown for Your Community"
          criteria={criteria as Criterion[]}
          variant="breakdown"
        />
      )}

      <RemainingInsightSections structure={structure} communityId={communityId} communityName={communityName} />
    </Container>
  );
};

export default RecommendationStructuredView;
